import { useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import moment from 'moment';
import { Eye, Star, X } from 'lucide-react';

import {
  listStudentFeedback,
  getFeedbackSummary,
  getFeedbackFilters,
  getReviewLists,
} from '../../../services/StudentFeedbackService';
import { ratingSegments } from './feedbackBands';
import ReviewCompareChart from './ReviewCompareChart';
import BandDonut from './BandDonut';
import ReviewsSection from './ReviewsSection';
import ReviewsPopup from './ReviewsPopup';
import { Skeleton, SkeletonGroup } from '../common/Skeleton/Skeleton.jsx';
/* Waypoint. An entry names the batch the session belonged to, which is
   the only record on a feedback row that is not deliberately anonymous.
   It lives in the popup rather than the row, because the row is already
   a button -- the one that opens the popup. */
import { useWaypoint } from '../common/waypoint/WaypointProvider.jsx';
import '../common/waypoint/waypoint.css';
/* .pg-modal -- the house popup. Imported here rather than assumed from
   whichever page renders this list. */
import '../Trainer/TrainerDashboard.css';
import './feedback-list.css';

/**
 * Session feedback, as a trainer or an administrator reads it.
 *
 * ONE component for both, because they are the same list of the same
 * rows and the server decides what each may see: a trainer gets their
 * own sessions with the student identity already stripped, an admin gets
 * everything with names. There is no `role` prop here on purpose — a
 * component that decided its own scope would be a component that could
 * be asked to show the wrong one.
 *
 * `variant` only changes the frame and how much of it is drawn:
 * "panel" sits inside a dashboard tab, "page" stands on its own, and
 * "widget" is the short form for a dashboard -- the five most recent
 * rows, no summary and no filters, with `onSeeAll` leading to the full
 * list. It reads the same rows through the same endpoint, so a trainer's
 * widget is anonymous for the same reason their tab is: the names never
 * leave the server.
 *
 * `trainerId` PINS the list to one trainer. It exists for one case: an
 * administrator reading somebody else's trainer dashboard. The server
 * scopes by the CALLER, and an admin's scope is everything -- so without
 * this the Feedback tab on trainer X's dashboard showed the whole
 * institution's feedback under a heading about trainer X.
 *
 * It is a filter, not a permission. The API still decides what this
 * caller may see and ignores a trainerId from anyone who is not an
 * admin, so pinning can only ever narrow what would have been returned.
 */

const PAGE_SIZE = 10;
// A dashboard widget is an aside, not a list: five rows, and a way through
// to the rest.
const WIDGET_SIZE = 5;
// How many batch + trainer pairs each review section lists before
// "View more".
const REVIEWS_PREVIEW = 5;
const EMPTY_TRAINERS = { items: [], total: 0, range: null };

const RatingStars = ({ value, label }) => {
  const n = Number(value) || 0;
  return (
    <span className="fbl-stars" role="img" aria-label={`${label}: ${n} out of 5`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <Star
          key={i}
          size={13}
          className={i <= n ? 'fbl-star is-on' : 'fbl-star'}
          aria-hidden="true"
        />
      ))}
      <b>{n || '—'}</b>
    </span>
  );
};

const formatWhen = (value, format) => (value ? moment(value).format(format) : '—');

/**
 * One feedback row, opened.
 *
 * Everything shown comes from the row the list already holds. The server
 * has decided what this reader may see: an admin's copy names the
 * student, a trainer's copy arrives without one -- so nothing is hidden
 * here, and nothing here could reveal it.
 */
const FeedbackDetail = ({ row, onClose, onOpenBatch }) => {
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== 'Escape') return;
      // A batch opened from inside the popup sits on top of it; Escape
      // belongs to that one first.
      if (document.querySelector('.wp-modal')) return;
      onClose();
    };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  /* Worked out by the API (studentfeedback.service.js, enrichFeedbackRows):
     the trainer's name, the session's topics and how each was rated. The
     popup lays them out and derives none of them. */
  const topicRows = Array.isArray(row.topicBreakdown) ? row.topicBreakdown : [];
  const topicCount = Number(row.sessionTopicCount) || 0;
  const ratedCount = Number(row.topicsRatedCount) || 0;

  const hasStudent = !row.anonymous && (row.studentName || row.studentEmail);

  return (
    <div className="pg-modal" role="presentation" onClick={onClose}>
      <div
        className="pg-modal-panel pg-modal-panel--wide"
        role="dialog"
        aria-modal="true"
        aria-labelledby="fbl-detail-title"
        onClick={(e) => e.stopPropagation()}
      >
        <header className="pg-modal-head">
          <div className="pg-modal-heading">
            <span className="pg-modal-eyebrow">Session feedback</span>
            <h3 className="pg-modal-title" id="fbl-detail-title">
              {row.batchName || 'Unknown batch'}
            </h3>
            <p className="pg-modal-sub">
              {formatWhen(row.date, 'dddd, D MMM YYYY')}
              {row.trainerName ? ` · Taught by ${row.trainerName}` : ''}
            </p>
          </div>
          <button type="button" className="pg-modal-close" aria-label="Close" onClick={onClose}>
            <X size={16} />
          </button>
        </header>

        <div className="pg-modal-body">
          <dl className="fbl-dl">
            <div className="fbl-dl-item">
              <dt>Batch</dt>
              <dd>
                {row.batchId ? (
                  <button type="button" className="wp-link" onClick={() => onOpenBatch('batch', row.batchId)}>
                    {row.batchName || 'Open batch'}
                  </button>
                ) : (
                  row.batchName || '—'
                )}
              </dd>
            </div>
            <div className="fbl-dl-item">
              <dt>Session date</dt>
              <dd>{formatWhen(row.date, 'D MMM YYYY')}</dd>
            </div>
            <div className="fbl-dl-item">
              <dt>Taught by</dt>
              <dd>{row.trainerName || 'Not recorded'}</dd>
            </div>
            <div className="fbl-dl-item">
              <dt>Topics in the session</dt>
              <dd>{topicCount}</dd>
            </div>
            {/* Present only on an admin's copy. A trainer's has no student
                on it at all -- the server strips it -- so it says so. */}
            {hasStudent ? (
              <div className="fbl-dl-item">
                <dt>Student</dt>
                <dd>
                  {row.studentName || '—'}
                  {row.studentEmail && <small>{row.studentEmail}</small>}
                </dd>
              </div>
            ) : row.anonymous ? (
              <div className="fbl-dl-item">
                <dt>Student</dt>
                <dd>Anonymous</dd>
              </div>
            ) : null}
            <div className="fbl-dl-item">
              <dt>Submitted</dt>
              <dd>{formatWhen(row.createdAt, 'D MMM YYYY, h:mm A')}</dd>
            </div>
            <div className="fbl-dl-item">
              <dt>Status</dt>
              <dd>{row.status || 'Submitted'}</dd>
            </div>
          </dl>

          <h3 className="fbl-section">Ratings</h3>
          <div className="fbl-ratings">
            <div className="fbl-fact">
              <span className="fbl-fact-label">Session</span>
              <RatingStars value={row.rating} label="Session" />
            </div>
            <div className="fbl-fact">
              <span className="fbl-fact-label">Trainer</span>
              {row.trainerRating ? (
                <RatingStars value={row.trainerRating} label="Trainer" />
              ) : (
                <span className="fbl-muted">Not rated</span>
              )}
            </div>
          </div>

          <h3 className="fbl-section">
            Topics covered <span className="fbl-count">{topicCount}</span>
          </h3>
          {topicCount === 0 && (
            <p className="fbl-muted">No topics were recorded for this session.</p>
          )}
          {topicRows.length > 0 && (
            <>
              <p className="fbl-topics-sum">
                {ratedCount} of {topicRows.length} rated individually
              </p>
              <ul className="fbl-topics">
                {topicRows.map((t, i) => (
                  <li key={`${t.topic}-${i}`}>
                    <div className="fbl-topic-row">
                      <span className="fbl-topic-name">
                        {t.topic}
                        {t.onRecord === false && <span className="fbl-tag fbl-tag--soft">Not on the session record</span>}
                      </span>
                      {Number(t.rating) > 0 ? (
                        <RatingStars value={t.rating} label={t.topic} />
                      ) : (
                        <span className="fbl-muted">Not rated</span>
                      )}
                    </div>
                    {t.comment && <p className="fbl-topic-note">{t.comment}</p>}
                  </li>
                ))}
              </ul>
            </>
          )}

          <h3 className="fbl-section">Comment</h3>
          {row.comment ? (
            <p className="fbl-prose">{row.comment}</p>
          ) : (
            <p className="fbl-muted">No comment written.</p>
          )}

          {row.trainerNote && (
            <>
              <h3 className="fbl-section">
                Reply{row.reviewedByRole ? ` · ${row.reviewedByRole}` : ''}
                {row.reviewedAt ? ` · ${formatWhen(row.reviewedAt, 'D MMM YYYY')}` : ''}
              </h3>
              <p className="fbl-prose">{row.trainerNote}</p>
            </>
          )}
        </div>

        <footer className="pg-modal-foot">
          <button type="button" className="fbl-page-btn" onClick={onClose}>
            Close
          </button>
        </footer>
      </div>
    </div>
  );
};

const FeedbackList = ({ variant = 'page', title, sub, trainerId: pinnedTrainerId, onSeeAll }) => {
  const compact = variant === 'widget';
  const { open: openEntity } = useWaypoint();
  const [rows, setRows] = useState([]);
  const [pagination, setPagination] = useState(null);
  const [summary, setSummary] = useState(null);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [failed, setFailed] = useState(false);
  // The row open in the popup, held whole: it already carries everything.
  const [viewing, setViewing] = useState(null);
  const closeView = useCallback(() => setViewing(null), []);

  /* Every one of these is sent to the API and applied in the query. None
     of it narrows the rows after they arrive: a page of ten filtered in
     the browser is a page of ten with some hidden, and the summary above
     it would then describe a different set. */
  const [filters, setFilters] = useState({
    dateFrom: '',
    dateTo: '',
    batchId: '',
    topic: '',
    rating: 'all',
    trainerId: '',
  });

  // Batches, trainers and topics that actually appear in this reader's
  // feedback, plus the scope the server applied.
  const [options, setOptions] = useState({ batches: [], trainers: [], topics: [], scope: '' });

  /* The reporting period the charts and the Bad / Good Reviews boxes
     cover, as "YYYY-MM-DDTHH:mm" for the datetime inputs. null until the
     API has said what the default is (6th of last month to the 5th of
     this one), so nothing is charted over the wrong span first. The list
     below keeps its own From / To. */
  const [period, setPeriod] = useState(null);
  const [defaultPeriod, setDefaultPeriod] = useState(null);

  // Which request the answer belongs to: a slow page must not land on
  // top of a filter chosen after it.
  const seq = useRef(0);

  /* The filters, as the API takes them. Empty values are dropped rather
     than sent blank -- a `batchId=` on the query string is a filter the
     server has to decide to ignore. */
  const queryFrom = (f) => ({
    ...(f.dateFrom ? { dateFrom: f.dateFrom } : {}),
    ...(f.dateTo ? { dateTo: f.dateTo } : {}),
    ...(f.batchId ? { batchId: f.batchId } : {}),
    ...(f.topic ? { topic: f.topic } : {}),
    ...(f.rating !== 'all' ? { rating: Number(f.rating) } : {}),
    /* The pin wins. A control cannot widen past it, because the pin is
       the question being asked: "what did students say about THIS
       trainer". */
    ...(pinnedTrainerId
      ? { trainerId: String(pinnedTrainerId) }
      : f.trainerId
        ? { trainerId: f.trainerId }
        : {}),
  });

  /* The page's filters with the reporting period in place of the list's
     own dates. Sent with the viewer's offset, so the API reads 00:00 as
     midnight here rather than midnight on the server. */
  const reportQuery = (f, p) => ({
    ...queryFrom(f),
    ...(p?.from ? { dateFrom: moment(p.from).format() } : {}),
    ...(p?.to ? { dateTo: moment(p.to).format() } : {}),
  });

  const load = useCallback(async (wanted, active) => {
    const mine = ++seq.current;
    setLoading(true);
    setFailed(false);
    try {
      const res = await listStudentFeedback({
        page: wanted,
        limit: compact ? WIDGET_SIZE : PAGE_SIZE,
        ...queryFrom(active),
      });
      if (mine !== seq.current) return;
      setRows(res.data);
      setPagination(res.pagination);
      setPage(wanted);
    } catch (error) {
      if (mine !== seq.current) return;
      setRows([]);
      setFailed(true);
    } finally {
      if (mine === seq.current) setLoading(false);
    }
  }, [compact]);

  useEffect(() => {
    load(1, filters);
    // pinnedTrainerId is a dependency because queryFrom() reads it: an
    // admin switching trainer on the dashboard must refetch, not keep
    // the previous trainer's rows under the new name.
  }, [load, filters, pinnedTrainerId]);

  /* The summary is fetched with the list's filters, except that its
     dates are the reporting period rather than the list's From / To --
     the charts report on a period, the list browses. */
  useEffect(() => {
    // The widget draws no summary; the page waits for its period.
    if (compact || !period) return undefined;
    let cancelled = false;
    getFeedbackSummary(reportQuery(filters, period)).then((s) => {
      if (!cancelled) setSummary(s);
    });
    return () => {
      cancelled = true;
    };
  }, [filters, period, pinnedTrainerId, compact]);

  /* The Bad Reviews and Good Reviews boxes, side by side -- the same
     filters and reporting period as the charts, so the two are one set. Not fetched
     for the widget, which draws neither. Each is the first five TRAINERS of
     its list, ranked by the API: the most bad reviews, and the most good
     (the gold "favourites"). */
  const [badTrainers, setBadTrainers] = useState(EMPTY_TRAINERS);
  const [goodTrainers, setGoodTrainers] = useState(EMPTY_TRAINERS);
  const [reviewsLoading, setReviewsLoading] = useState(false);
  // Which section's View more popup is open: 'bad', 'good' or null.
  const [reviewsPopup, setReviewsPopup] = useState(null);
  const closeReviewsPopup = useCallback(() => setReviewsPopup(null), []);
  useEffect(() => {
    if (compact || !period) return undefined;
    let cancelled = false;
    setReviewsLoading(true);
    const params = reportQuery(filters, period);
    // Each box shows five trainers; View more has the rest.
    const firstFive = (kind) =>
      getReviewLists({ ...params, kind, groupBy: 'trainer', page: 1, limit: REVIEWS_PREVIEW })
        .then((r) => ({ items: r.data, total: Number(r.pagination?.total) || 0, range: r.range }))
        .catch(() => EMPTY_TRAINERS);
    Promise.all([firstFive('bad'), firstFive('good')]).then(([bad, good]) => {
      if (cancelled) return;
      setBadTrainers(bad);
      setGoodTrainers(good);
      setReviewsLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [filters, period, pinnedTrainerId, compact]);

  useEffect(() => {
    let cancelled = false;
    getFeedbackFilters().then((o) => {
      if (cancelled) return;
      setOptions(o || { batches: [], trainers: [], topics: [], scope: '' });
      /* An older API without reportPeriod still gets a page: an open
         period, the whole set, rather than charts that never load. */
      const p = o?.reportPeriod || { from: '', to: '' };
      setDefaultPeriod(p);
      setPeriod((prev) => prev || p);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  const set = (key, value) => setFilters((prev) => ({ ...prev, [key]: value }));

  const anyFilter =
    Boolean(filters.dateFrom || filters.dateTo || filters.batchId || filters.topic || filters.trainerId)
    || filters.rating !== 'all';

  const clearFilters = () =>
    setFilters({ dateFrom: '', dateTo: '', batchId: '', topic: '', rating: 'all', trainerId: '' });

  const totalPages = pagination?.totalPages || 1;

  return (
    <section className={`fbl fbl--${variant}`}>
      {(title || sub) && (
        <header className="fbl-head">
          <div>
            {title && <h2 className="fbl-title">{title}</h2>}
            {sub && <p className="fbl-sub">{sub}</p>}
          </div>
        </header>
      )}

      {/* The reporting period: what the charts and the Bad / Good Reviews
          boxes below cover. Opens on the API's default and can be moved
          to any date and time; Default puts it back. */}
      {!compact && period && (
        <div className="fbl-tools">
          <div className="fbl-field">
            <label className="fbl-tools-label" htmlFor="fbl-period-from">Report from</label>
            <input
              id="fbl-period-from"
              type="datetime-local"
              className="fbl-select"
              value={period.from}
              max={period.to || undefined}
              onChange={(e) => setPeriod((p) => ({ ...p, from: e.target.value }))}
            />
          </div>

          <div className="fbl-field">
            <label className="fbl-tools-label" htmlFor="fbl-period-to">Report to</label>
            <input
              id="fbl-period-to"
              type="datetime-local"
              className="fbl-select"
              value={period.to}
              min={period.from || undefined}
              onChange={(e) => setPeriod((p) => ({ ...p, to: e.target.value }))}
            />
          </div>

          {defaultPeriod &&
            (period.from !== defaultPeriod.from || period.to !== defaultPeriod.to) && (
              <button
                type="button"
                className="fbl-page-btn fbl-clear"
                onClick={() => setPeriod(defaultPeriod)}
              >
                Default period
              </button>
            )}
        </div>
      )}

      {/* What the feedback says in total, before any one row of it.
          Not in the widget: the dashboard it sits on already carries the
          verdict, and saying it twice on one screen says it less. */}
      {!compact && (
      <div className="fbl-summary">
        <div className="fbl-score">
          <b>{summary?.averageRating ? Number(summary.averageRating).toFixed(1) : '—'}</b>
          <span>out of 5</span>
          <small>
            {summary?.total
              ? `${summary.total} ${summary.total === 1 ? 'response' : 'responses'}`
              : 'No responses yet'}
          </small>
        </div>

        {/* The shape of the whole, drawn from the summary rather than
            the page in hand: a page is ten rows, and how the ratings
            split is a question about all of them. */}
        <div className="fbl-chart fbl-chart--ratings">
          <p className="fbl-chart-title">Ratings</p>
          <BandDonut
            segments={ratingSegments(summary?.ratingGroups)}
            centreValue={Number(summary?.total) || 0}
            centreLabel={Number(summary?.total) === 1 ? 'response' : 'responses'}
            empty="No ratings to chart yet."
          />
        </div>

        {/* Good against bad, on the same line the Bad / Good Reviews boxes
            below draw (4-5 good, 1-3 bad). The counts and shares are the
            API's (summary().reviewGroups) over every matching row -- not
            worked out from the rows this page happens to have loaded. */}
        <div className="fbl-chart fbl-chart--compare">
          <p className="fbl-chart-title">Good vs bad feedbacks</p>
          <ReviewCompareChart groups={summary?.reviewGroups} />
        </div>

        {Number(summary?.lowRatingCount) > 0 && (
          <p className="fbl-low">
            {summary.lowRatingCount} rated 2 or below
          </p>
        )}
      </div>
      )}

      {/* Bad Reviews and Good Reviews, side by side: five of each, a
          Reviews button on every item, and View more for the popup with
          every trainer and batch. */}
      {!compact && (
        <div className="brs-pair">
          <ReviewsSection
            kind="bad"
            items={badTrainers.items}
            total={badTrainers.total}
            range={badTrainers.range}
            loading={reviewsLoading}
            baseParams={reportQuery(filters, period)}
            onView={() => setReviewsPopup('bad')}
          />
          <ReviewsSection
            kind="good"
            items={goodTrainers.items}
            total={goodTrainers.total}
            range={goodTrainers.range}
            loading={reviewsLoading}
            baseParams={reportQuery(filters, period)}
            onView={() => setReviewsPopup('good')}
          />
        </div>
      )}

      {!compact && (
      <div className="fbl-tools">
        <div className="fbl-field">
          <label className="fbl-tools-label" htmlFor="fbl-from">From</label>
          <input
            id="fbl-from"
            type="date"
            className="fbl-select"
            value={filters.dateFrom}
            max={filters.dateTo || undefined}
            onChange={(e) => set('dateFrom', e.target.value)}
          />
        </div>

        <div className="fbl-field">
          <label className="fbl-tools-label" htmlFor="fbl-to">To</label>
          <input
            id="fbl-to"
            type="date"
            className="fbl-select"
            value={filters.dateTo}
            min={filters.dateFrom || undefined}
            onChange={(e) => set('dateTo', e.target.value)}
          />
        </div>

        <div className="fbl-field fbl-field--grow">
          <label className="fbl-tools-label" htmlFor="fbl-batch">Batch</label>
          <select
            id="fbl-batch"
            className="fbl-select"
            value={filters.batchId}
            onChange={(e) => set('batchId', e.target.value)}
          >
            <option value="">All batches</option>
            {options.batches.map((b) => (
              <option key={b.id} value={b.id}>{b.name}</option>
            ))}
          </select>
        </div>

        <div className="fbl-field fbl-field--grow">
          <label className="fbl-tools-label" htmlFor="fbl-topic">Topic</label>
          <select
            id="fbl-topic"
            className="fbl-select"
            value={filters.topic}
            onChange={(e) => set('topic', e.target.value)}
          >
            <option value="">All topics</option>
            {options.topics.map((t) => (
              <option key={t} value={t}>{t.length > 70 ? `${t.slice(0, 69)}…` : t}</option>
            ))}
          </select>
        </div>

        <div className="fbl-field">
          <label className="fbl-tools-label" htmlFor="fbl-rating">Rating</label>
          <select
            id="fbl-rating"
            className="fbl-select"
            value={filters.rating}
            onChange={(e) => set('rating', e.target.value)}
          >
            <option value="all">All ratings</option>
            {[5, 4, 3, 2, 1].map((n) => (
              <option key={n} value={String(n)}>
                {n} star{n === 1 ? '' : 's'}
              </option>
            ))}
          </select>
        </div>

        {/* Administrators only, and that is the SERVER's answer, not a
            guess made here: filters() builds the trainer list for an
            admin and returns it empty for anyone else, because listing
            colleagues to a trainer would leak who else is being
            reviewed. The control is drawn from what came back. */}
        {/* Hidden while pinned: the answer is already fixed, and a
            control that cannot change it is a control that lies. */}
        {!pinnedTrainerId && options.scope === 'admin' && options.trainers.length > 0 && (
          <div className="fbl-field fbl-field--grow">
            <label className="fbl-tools-label" htmlFor="fbl-trainer">Trainer</label>
            <select
              id="fbl-trainer"
              className="fbl-select"
              value={filters.trainerId}
              onChange={(e) => set('trainerId', e.target.value)}
            >
              <option value="">All trainers</option>
              {options.trainers.map((t) => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        )}

        {anyFilter && (
          <button type="button" className="fbl-page-btn fbl-clear" onClick={clearFilters}>
            Clear
          </button>
        )}
      </div>
      )}

      {loading && rows.length === 0 ? (
        /* The list's own items, shimmering: the same bordered .fbl-item
           and the same two-sided head, so the page does not reflow when
           the feedback lands. */
        <SkeletonGroup label="Loading feedback">
          <ul className="fbl-list">
            {(compact ? [0, 1, 2] : [0, 1, 2, 3, 4, 5]).map((i) => (
              <li className="fbl-item" key={i}>
                <div className="fbl-item-head fbl-item-head--skel">
                  <span className="fbl-item-id lms-skel-grow">
                    <Skeleton shape="title" width={i % 2 ? '40' : '55'} />
                    <Skeleton width={i % 2 ? '70' : '55'} />
                  </span>
                  <span className="fbl-item-scores">
                    <Skeleton shape="pill" />
                    <Skeleton shape="pill" />
                  </span>
                </div>
              </li>
            ))}
          </ul>
        </SkeletonGroup>
      ) : failed ? (
        <p className="fbl-note">That did not load. Try again in a moment.</p>
      ) : rows.length === 0 ? (
        <p className="fbl-note">
          {anyFilter
            ? 'Nothing matches these filters.'
            : 'No feedback has been left yet.'}
        </p>
      ) : (
        <ul className="fbl-list">
          {rows.map((row) => {
            const topicCount = Number(row.sessionTopicCount) || 0;
            const meta = [
              moment(row.date).format('D MMM YYYY'),
              row.trainerName ? `Taught by ${row.trainerName}` : null,
              `${topicCount} ${topicCount === 1 ? 'topic' : 'topics'}`,
              // Only an admin's copy carries a name; a trainer's never does.
              row.studentName ? `From ${row.studentName}` : null,
            ].filter(Boolean);
            return (
              <li className="fbl-item" key={row.id}>
                <button
                  type="button"
                  className="fbl-item-head"
                  onClick={() => setViewing(row)}
                  aria-haspopup="dialog"
                >
                  <span className="fbl-item-id">
                    <span className="fbl-item-name">{row.batchName || 'Unknown batch'}</span>
                    <span className="fbl-item-when">{meta.join(' · ')}</span>
                  </span>

                  <span className="fbl-item-scores">
                    <RatingStars value={row.rating} label="Session" />
                    {row.status === 'Reviewed' && (
                      <span className="fbl-tag">Reviewed</span>
                    )}
                    <span className="fbl-view">
                      <Eye size={14} aria-hidden="true" />
                      View
                    </span>
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {compact && onSeeAll && rows.length > 0 && (
        <div className="fbl-pager">
          <button type="button" className="fbl-page-btn" onClick={onSeeAll}>
            See all feedback
          </button>
        </div>
      )}

      {!compact && totalPages > 1 && (
        <div className="fbl-pager">
          <button
            type="button"
            className="fbl-page-btn"
            disabled={page <= 1 || loading}
            onClick={() => load(page - 1, filters)}
          >
            Previous
          </button>
          <span className="fbl-page-at">
            Page {page} of {totalPages}
          </span>
          <button
            type="button"
            className="fbl-page-btn"
            disabled={page >= totalPages || loading}
            onClick={() => load(page + 1, filters)}
          >
            Next
          </button>
        </div>
      )}

      {/* Out to the body, always.

          .pg-modal is position: fixed, which is only fixed to the VIEWPORT
          while no ancestor holds a transform. The staff overlay's panel
          animates in with one and `both` leaves it filling, so the popup
          became a box inside a 460px panel and was then cut off by that
          panel's overflow -- it opened under the widget rather than over
          the page. A portal answers that for every place this list is
          drawn, rather than asking each of them not to transform. */}
      {viewing && createPortal(
        <FeedbackDetail row={viewing} onClose={closeView} onOpenBatch={openEntity} />,
        document.body,
      )}

      {/* Same portal, for the same reason. It takes the page's filters so
          its lists and the sections are one set. */}
      {reviewsPopup && createPortal(
        <ReviewsPopup kind={reviewsPopup} params={reportQuery(filters, period)} onClose={closeReviewsPopup} />,
        document.body,
      )}
    </section>
  );
};

export default FeedbackList;
